/**
 * Hierarchy Management Component
 * ==============================
 * Manages user hierarchy (Account Owner -> Team Lead -> Recruiter) and profile ownership transfers.
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const HierarchyManagement = ({ currentUser }) => {
  const [activeTab, setActiveTab] = useState('hierarchy');
  const [hierarchy, setHierarchy] = useState(null);
  const [users, setUsers] = useState([]);
  const [leavingUsers, setLeavingUsers] = useState([]);
  const [transferHistory, setTransferHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Transfer modal state
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [transferPreview, setTransferPreview] = useState(null);
  const [transferForm, setTransferForm] = useState({
    from_user_id: '',
    to_user_id: '',
    reason: ''
  });

  // Manager assignment modal
  const [showManagerModal, setShowManagerModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const token = localStorage.getItem('auth_token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [hierarchyRes, leavingRes, historyRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/hierarchy/tree`, { headers }),
        axios.get(`${BACKEND_URL}/api/hierarchy/leaving-users`, { headers }),
        axios.get(`${BACKEND_URL}/api/transfer/history`, { headers })
      ]);

      setHierarchy(hierarchyRes.data);
      setLeavingUsers(leavingRes.data.users || []);
      setTransferHistory(historyRes.data.transfers || []);

      // Build flat user list for dropdowns
      const allUsers = [];
      hierarchyRes.data.admins?.forEach(u => allUsers.push(u));
      hierarchyRes.data.managers?.forEach(m => {
        allUsers.push(m.manager);
        m.team?.forEach(t => allUsers.push(t));
      });
      hierarchyRes.data.unassigned?.forEach(u => allUsers.push(u));
      setUsers(allUsers);

    } catch (err) {
      console.error('Failed to fetch hierarchy data:', err);
      setError(err.response?.data?.detail || 'Failed to load hierarchy data');
    } finally {
      setLoading(false);
    }
  };

  // Set manager for a user
  const handleSetManager = async (userId, managerId) => {
    try {
      await axios.post(`${BACKEND_URL}/api/hierarchy/set-manager`, {
        user_id: userId,
        manager_id: managerId
      }, { headers });

      alert('Manager assigned successfully');
      setShowManagerModal(false);
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to assign manager');
    }
  };

  // Update employment status
  const handleUpdateEmploymentStatus = async (userId, status, departureDate = null) => {
    try {
      await axios.patch(`${BACKEND_URL}/api/hierarchy/users/${userId}/employment-status`, {
        employment_status: status,
        departure_date: departureDate
      }, { headers });

      alert(`Employment status updated to ${status}`);
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to update status');
    }
  };

  // Preview transfer
  const handlePreviewTransfer = async () => {
    if (!transferForm.from_user_id || !transferForm.to_user_id) {
      alert('Please select both source and target users');
      return;
    }

    try {
      const response = await axios.post(`${BACKEND_URL}/api/transfer/preview`, {
        from_user_id: transferForm.from_user_id,
        to_user_id: transferForm.to_user_id
      }, { headers });

      setTransferPreview(response.data);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to preview transfer');
    }
  };

  // Execute transfer
  const handleExecuteTransfer = async () => {
    if (!transferForm.reason) {
      alert('Please provide a reason for the transfer');
      return;
    }

    const confirmText = `CONFIRM`;
    const userInput = prompt(
      `You are about to transfer ${transferPreview.profile_count} profiles from ` +
      `${transferPreview.from_user.full_name} to ${transferPreview.to_user.full_name}.\n\n` +
      `Type "CONFIRM" to proceed:`
    );

    if (userInput !== confirmText) {
      alert('Transfer cancelled');
      return;
    }

    try {
      await axios.post(`${BACKEND_URL}/api/transfer/bulk`, {
        from_user_id: transferForm.from_user_id,
        to_user_id: transferForm.to_user_id,
        reason: transferForm.reason
      }, { headers });

      alert(`Successfully transferred ${transferPreview.profile_count} profiles`);
      setShowTransferModal(false);
      setTransferPreview(null);
      setTransferForm({ from_user_id: '', to_user_id: '', reason: '' });
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to execute transfer');
    }
  };

  const getRoleIcon = (role) => {
    switch(role) {
      case 'admin': return '👑';
      case 'manager': return '📋';
      case 'recruiter': return '👤';
      case 'auditor': return '🔍';
      case 'viewer': return '👁️';
      default: return '❓';
    }
  };

  const getRoleName = (role) => {
    switch(role) {
      case 'admin': return 'Account Owner';
      case 'manager': return 'Team Lead';
      case 'recruiter': return 'Recruiter';
      default: return role;
    }
  };

  const getEmploymentStatusBadge = (status) => {
    switch(status) {
      case 'active':
        return <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-500/15 text-green-400 border border-green-500/25">Active</span>;
      case 'leaving':
        return <span className="px-2 py-1 text-xs font-medium rounded-full bg-amber-500/15 text-amber-400 border border-amber-500/25">Leaving</span>;
      case 'departed':
        return <span className="px-2 py-1 text-xs font-medium rounded-full bg-red-500/15 text-red-400 border border-red-500/25">Departed</span>;
      default:
        return <span className="px-2 py-1 text-xs font-medium rounded-full bg-subtle text-ink-muted border border-line">{status}</span>;
    }
  };

  if (loading) {
    return (
      <div className="bg-surface border border-line rounded-xl shadow-sm p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-4 bg-subtle rounded w-1/4"></div>
          <div className="h-32 bg-subtle rounded"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-surface border border-line rounded-xl shadow-sm p-8">
        <div className="text-red-400">
          <p className="font-semibold">Error loading hierarchy</p>
          <p className="text-sm">{error}</p>
          <button
            onClick={fetchData}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-elevated border border-line rounded-xl shadow-sm p-6">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold text-ink">Hierarchy & Transfers</h2>
            <p className="text-ink-muted">Manage team hierarchy and profile ownership</p>
          </div>
          <button
            onClick={() => setShowTransferModal(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-semibold"
          >
            Transfer Profiles
          </button>
        </div>

        {/* Tab Navigation */}
        <div className="flex space-x-4 mt-6 border-b border-line">
          {['hierarchy', 'leaving', 'history'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`pb-3 px-4 font-medium capitalize ${
                activeTab === tab
                  ? 'text-blue-400 border-b-2 border-blue-500'
                  : 'text-ink-subtle hover:text-ink'
              }`}
            >
              {tab === 'leaving' ? 'Leaving Users' : tab === 'history' ? 'Transfer History' : 'Hierarchy Tree'}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'hierarchy' && hierarchy && (
        <div className="bg-surface border border-line rounded-xl shadow-sm p-6">
          <h3 className="text-lg font-semibold text-ink mb-4">Organization Hierarchy</h3>

          {/* Stats */}
          <div className="grid grid-cols-4 gap-4 mb-6">
            <div className="bg-muted border border-line p-4 rounded-lg">
              <div className="text-2xl font-bold text-blue-400">{hierarchy.stats?.total_admins || 0}</div>
              <div className="text-sm text-ink-muted">Account Owners</div>
            </div>
            <div className="bg-muted border border-line p-4 rounded-lg">
              <div className="text-2xl font-bold text-blue-400">{hierarchy.stats?.total_managers || 0}</div>
              <div className="text-sm text-ink-muted">Team Leads</div>
            </div>
            <div className="bg-muted border border-line p-4 rounded-lg">
              <div className="text-2xl font-bold text-green-400">{hierarchy.stats?.total_tacs || 0}</div>
              <div className="text-sm text-ink-muted">Recruiters</div>
            </div>
            <div className="bg-muted border border-line p-4 rounded-lg">
              <div className="text-2xl font-bold text-amber-400">{hierarchy.stats?.unassigned_tacs || 0}</div>
              <div className="text-sm text-ink-muted">Unassigned</div>
            </div>
          </div>

          {/* Hierarchy Tree */}
          <div className="space-y-4">
            {/* Business Owners */}
            {hierarchy.admins?.map((admin) => (
              <div key={admin.id} className="border-l-4 border-line pl-4 py-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <span className="text-xl">{getRoleIcon(admin.role)}</span>
                    <span className="font-semibold text-ink">{admin.full_name}</span>
                    <span className="text-ink-subtle text-sm">({admin.email})</span>
                    {getEmploymentStatusBadge(admin.employment_status)}
                  </div>
                  <span className="text-sm text-blue-400 font-medium">Account Owner</span>
                </div>
              </div>
            ))}

            {/* Managers with Teams */}
            {hierarchy.managers?.map((managerData) => (
              <div key={managerData.manager.id} className="border-l-4 border-line pl-4 py-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <span className="text-xl">{getRoleIcon(managerData.manager.role)}</span>
                    <span className="font-semibold text-ink">{managerData.manager.full_name}</span>
                    <span className="text-ink-subtle text-sm">({managerData.manager.email})</span>
                    {getEmploymentStatusBadge(managerData.manager.employment_status)}
                  </div>
                  <span className="text-sm text-blue-400 font-medium">
                    Team Lead ({managerData.team_size} Recruiters)
                  </span>
                </div>

                {/* Team Members */}
                {managerData.team?.length > 0 && (
                  <div className="ml-6 mt-2 space-y-2">
                    {managerData.team.map((tac) => (
                      <div key={tac.id} className="border-l-2 border-line pl-4 py-1 flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <span>{getRoleIcon(tac.role)}</span>
                          <span className="text-ink">{tac.full_name}</span>
                          <span className="text-ink-subtle text-sm">({tac.email})</span>
                          {getEmploymentStatusBadge(tac.employment_status)}
                        </div>
                        <div className="flex items-center space-x-2">
                          {tac.employment_status === 'active' && (
                            <button
                              onClick={() => {
                                const date = prompt('Enter departure date (YYYY-MM-DD):');
                                if (date) {
                                  handleUpdateEmploymentStatus(tac.id, 'leaving', date + 'T00:00:00Z');
                                }
                              }}
                              className="text-xs text-amber-400 hover:text-amber-300"
                            >
                              Mark Leaving
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}

            {/* Unassigned Recruiters */}
            {hierarchy.unassigned?.length > 0 && (
              <div className="border-l-4 border-line pl-4 py-2">
                <div className="font-semibold text-ink-muted mb-2">Unassigned Recruiters</div>
                {hierarchy.unassigned.map((tac) => (
                  <div key={tac.id} className="flex items-center justify-between py-1">
                    <div className="flex items-center space-x-2">
                      <span>{getRoleIcon(tac.role)}</span>
                      <span className="text-ink">{tac.full_name}</span>
                      <span className="text-ink-subtle text-sm">({tac.email})</span>
                    </div>
                    <button
                      onClick={() => {
                        setSelectedUser(tac);
                        setShowManagerModal(true);
                      }}
                      className="text-xs bg-blue-500/15 text-blue-400 border border-blue-500/25 px-2 py-1 rounded hover:bg-blue-500/25"
                    >
                      Assign Manager
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'leaving' && (
        <div className="bg-surface border border-line rounded-xl shadow-sm p-6">
          <h3 className="text-lg font-semibold text-ink mb-4">Users Scheduled to Leave</h3>

          {leavingUsers.length === 0 ? (
            <p className="text-ink-subtle">No users are currently marked as leaving.</p>
          ) : (
            <div className="space-y-4">
              {leavingUsers.map((user) => (
                <div key={user.id} className="border border-amber-500/25 bg-amber-500/10 rounded-lg p-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="font-semibold text-ink">{user.full_name}</div>
                      <div className="text-sm text-ink-muted">{user.email}</div>
                      <div className="text-sm text-ink-subtle mt-1">
                        Role: {getRoleName(user.role)}
                      </div>
                      {user.departure_date && (
                        <div className="text-sm text-amber-400 mt-1">
                          Departure: {new Date(user.departure_date).toLocaleDateString()}
                        </div>
                      )}
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => {
                          setTransferForm({
                            from_user_id: user.id,
                            to_user_id: '',
                            reason: 'Employee departure'
                          });
                          setShowTransferModal(true);
                        }}
                        className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700"
                      >
                        Transfer Profiles
                      </button>
                      <button
                        onClick={() => handleUpdateEmploymentStatus(user.id, 'active')}
                        className="bg-muted text-ink-muted border border-line px-3 py-1 rounded text-sm hover:bg-subtle hover:text-ink"
                      >
                        Cancel Departure
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'history' && (
        <div className="bg-surface border border-line rounded-xl shadow-sm p-6">
          <h3 className="text-lg font-semibold text-ink mb-4">Transfer History</h3>

          {transferHistory.length === 0 ? (
            <p className="text-ink-subtle">No transfers have been performed yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-line">
                    <th className="text-left py-2 px-4 text-sm font-semibold text-ink-muted">Date</th>
                    <th className="text-left py-2 px-4 text-sm font-semibold text-ink-muted">From</th>
                    <th className="text-left py-2 px-4 text-sm font-semibold text-ink-muted">To</th>
                    <th className="text-left py-2 px-4 text-sm font-semibold text-ink-muted">Profiles</th>
                    <th className="text-left py-2 px-4 text-sm font-semibold text-ink-muted">Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {transferHistory.map((transfer) => (
                    <tr key={transfer.id} className="border-b border-line hover:bg-muted">
                      <td className="py-3 px-4 text-sm text-ink">
                        {new Date(transfer.timestamp).toLocaleString()}
                      </td>
                      <td className="py-3 px-4 text-sm text-ink">{transfer.from_user_id}</td>
                      <td className="py-3 px-4 text-sm text-ink">{transfer.to_user_id}</td>
                      <td className="py-3 px-4 text-sm font-medium text-ink">{transfer.profile_count}</td>
                      <td className="py-3 px-4 text-sm text-ink-muted">{transfer.reason}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Transfer Modal */}
      {showTransferModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-surface border border-line rounded-xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-bold text-ink mb-4">Transfer Profile Ownership</h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-ink-muted mb-1">
                  From User (Source)
                </label>
                <select
                  value={transferForm.from_user_id}
                  onChange={(e) => {
                    setTransferForm({ ...transferForm, from_user_id: e.target.value });
                    setTransferPreview(null);
                  }}
                  className="w-full px-3 py-2 bg-surface text-ink placeholder:text-ink-subtle border border-line rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:[color-scheme:dark]"
                >
                  <option value="">Select user...</option>
                  {users.filter(u => u.role === 'recruiter' || u.role === 'manager').map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.full_name} ({getRoleName(user.role)})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-ink-muted mb-1">
                  To User (Target)
                </label>
                <select
                  value={transferForm.to_user_id}
                  onChange={(e) => {
                    setTransferForm({ ...transferForm, to_user_id: e.target.value });
                    setTransferPreview(null);
                  }}
                  className="w-full px-3 py-2 bg-surface text-ink placeholder:text-ink-subtle border border-line rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:[color-scheme:dark]"
                >
                  <option value="">Select user...</option>
                  {users
                    .filter(u => (u.role === 'recruiter' || u.role === 'manager') && u.id !== transferForm.from_user_id)
                    .map((user) => (
                      <option key={user.id} value={user.id}>
                        {user.full_name} ({getRoleName(user.role)})
                      </option>
                    ))}
                </select>
              </div>

              {!transferPreview && (
                <button
                  onClick={handlePreviewTransfer}
                  className="w-full bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700"
                >
                  Preview Transfer
                </button>
              )}

              {transferPreview && (
                <div className="border border-blue-500/20 bg-blue-500/10 rounded-lg p-4">
                  <h4 className="font-semibold text-ink mb-2">Transfer Preview</h4>
                  <div className="text-sm text-ink-muted space-y-1">
                    <p>From: <strong className="text-ink">{transferPreview.from_user.full_name}</strong></p>
                    <p>To: <strong className="text-ink">{transferPreview.to_user.full_name}</strong></p>
                    <p>Profiles: <strong className="text-ink">{transferPreview.profile_count}</strong></p>
                  </div>

                  {transferPreview.profile_count > 0 && (
                    <div className="mt-3">
                      <label className="block text-sm font-medium text-ink-muted mb-1">
                        Reason for Transfer
                      </label>
                      <input
                        type="text"
                        value={transferForm.reason}
                        onChange={(e) => setTransferForm({ ...transferForm, reason: e.target.value })}
                        placeholder="e.g., Employee departure, Workload balancing"
                        className="w-full px-3 py-2 bg-surface text-ink placeholder:text-ink-subtle border border-line rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />

                      <button
                        onClick={handleExecuteTransfer}
                        className="w-full mt-3 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700"
                      >
                        Execute Transfer
                      </button>
                    </div>
                  )}

                  {transferPreview.profile_count === 0 && (
                    <p className="text-amber-400 mt-2 text-sm">
                      No profiles to transfer from this user.
                    </p>
                  )}
                </div>
              )}
            </div>

            <button
              onClick={() => {
                setShowTransferModal(false);
                setTransferPreview(null);
                setTransferForm({ from_user_id: '', to_user_id: '', reason: '' });
              }}
              className="mt-4 w-full bg-muted text-ink-muted border border-line py-2 rounded-lg hover:bg-subtle hover:text-ink"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Manager Assignment Modal */}
      {showManagerModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-surface border border-line rounded-xl p-6 w-full max-w-md">
            <h3 className="text-xl font-bold text-ink mb-4">Assign Manager</h3>
            <p className="text-ink-muted mb-4">
              Assign a manager to <strong className="text-ink">{selectedUser.full_name}</strong>
            </p>

            <div className="space-y-4">
              <select
                onChange={(e) => {
                  if (e.target.value) {
                    handleSetManager(selectedUser.id, e.target.value);
                  }
                }}
                className="w-full px-3 py-2 bg-surface text-ink placeholder:text-ink-subtle border border-line rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:[color-scheme:dark]"
              >
                <option value="">Select manager...</option>
                {users
                  .filter(u => u.role === 'manager' || u.role === 'admin')
                  .map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.full_name} ({getRoleName(user.role)})
                    </option>
                  ))}
              </select>
            </div>

            <button
              onClick={() => {
                setShowManagerModal(false);
                setSelectedUser(null);
              }}
              className="mt-4 w-full bg-muted text-ink-muted border border-line py-2 rounded-lg hover:bg-subtle hover:text-ink"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default HierarchyManagement;
